import { Type } from "@themes/pollinator/ui/Type";
import { TextItem } from "@themes/pollinator/ui/TextItem";
import { CallToAction } from "@themes/pollinator/ui/CallToAction";
import halloranLogo from "@themes/pollinator/assets/logos/logo_halloranPhilanthropies.png";
import jeromeLogo from "@themes/pollinator/assets/logos/logo_jeromeFoundation.png";
import swissPhilanthropiesLogo from "@themes/pollinator/assets/logos/logo_swissPhilanthropiesFoundation.png";
import { MarketingSection } from "../../components/Section";
import { MarketingAbout } from "../../components/About";
import { MarketingVision } from "../../components/Vision";
import type { FeaturedOrganization } from "@core/features/organization";

export type SectionItem = {
  title: string;
  description: string;
  subheader?: string;
};

export type SectionsContent = {
  howItWorks: SectionItem[];
  praise: SectionItem[];
  team: SectionItem[];
  advisors: SectionItem[];
};

export type Props = {
  onAddYourInstitutions: () => void;
  onDonateClick: () => void;
  onHireUsClick: () => void;
  onNavigationAnchorClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => void;
  onNewsletterClick: () => void;
  sectionsContent: SectionsContent;
  featuredOrganizations: FeaturedOrganization[];
};

export function MarketingContent({
  onAddYourInstitutions,
  onDonateClick,
  onHireUsClick,
  onNavigationAnchorClick,
  onNewsletterClick,
  sectionsContent,
  featuredOrganizations,
}: Props) {
  return (
    <>
      <div className="flex flex-col gap-8">
        {/* PROGRAM */}
        <MarketingSection title="Program" id="program">
          <Type variant="body1" className="max-w-3xl">
            Pollinator's program is like a virtual residency of mutual support.
            Once enrolled, we match you with a cohort of aligned peers who
            you'll meet with weekly. We handle all the logistics, and our
            artist-designed platform and conversation prompts help you build
            trust, real relationships, and a deeper network of mutual support.
          </Type>
          <Type variant="body1" className="max-w-3xl">
            To participate, you must commit to 90-minute virtual roundtable
            discussions on Fridays in May:
          </Type>
          <ul className="list-disc list-inside flex flex-col gap-2">
            <Type variant="body1" as="li" className="max-w-3xl">
              May 8 – welcome
            </Type>
            <Type variant="body1" as="li" className="max-w-3xl">
              May 15 – roundtable 1
            </Type>
            <Type variant="body1" as="li" className="max-w-3xl">
              May 22 – roundtable 2
            </Type>
            <Type variant="body1" as="li" className="max-w-3xl">
              May 29 – roundtable 3
            </Type>
            <Type variant="body1" as="li" className="max-w-3xl">
              June 5 – closing
            </Type>
          </ul>
          <Type variant="body1" className="max-w-3xl">
            Curious how Pollinator's 2025 pilot program went?{" "}
            <a
              href="https://pollinator-production.s3.us-east-1.amazonaws.com/production/assets/materials/PollinatorPilotClosingSession.pdf"
              className="underline hover:text-brand-700"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the report here.
            </a>
          </Type>
        </MarketingSection>
        <MarketingSection
          title="How Pollinator Works"
          id="how-pollinator-works"
        >
          <div className="flex flex-col gap-4 md:gap-8">
            {sectionsContent.howItWorks.map(({ title, description }) => (
              <TextItem key={title} title={title}>
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection>

        {/* FIT */}
        <MarketingSection title="Is Pollinator for Me?" id="fit">
          <Type variant="body1" className="max-w-3xl">
            If you check all of the below boxes, you're likely a fit for
            Pollinator:
          </Type>
          <ul className="flex flex-col gap-4 max-w-3xl">
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'm an alumnus or staff member from an organization in
              Pollinator's network*.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              My work focuses on art, culture, technology, ecology, or social
              impact.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'm able to access reliable wifi for 90-minute video calls on
              Fridays in May.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>I believe that sharing my
              experiences with my peers is a meaningful way to grow my practice.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'm open to new perspectives, and am willing to consider
              viewpoints that might challenge my current way of thinking or
              being.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'm open to self-discovery and growth; I am genuinely interested
              in better understanding myself.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'll speak up about my needs. If something isn't working, I'm
              committed to sharing that feedback and making clear requests to
              get what I need.
            </Type>
            <Type variant="body2" as="li" className="flex gap-3">
              <span className="shrink-0">&#9744;</span>
              I'll follow the community guidelines: (1) No bullying, (2) No hate
              speech, (3) Respect your peers' confidentiality, (4) Be open to
              repair, and (5) Show up for each other.
            </Type>
          </ul>
        </MarketingSection>

        {/* NETWORK */}
        <MarketingSection title="Network" id="network">
          <Type variant="body1" className="max-w-3xl">
            To build intentional community at scale, Pollinator is only open to
            alumni, cultural workers, and leaders from select residencies,
            fellowships, and schools. Each enrolled participant can also
            nominate up to five aligned peers to join Pollinator.
          </Type>
          <Type variant="body1" className="max-w-3xl">
            Pollinator's 2026 spring program is open to participants from over
            200 organizations, including:
          </Type>
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-x-4">
            {featuredOrganizations.map(({ name, location }) => (
              <div key={name} className="break-inside-avoid pb-2 md:pb-4">
                <Type variant={"h5"} as="div">
                  {name}
                </Type>
                {location && (
                  <Type variant="caption" as="div" className="uppercase">
                    {location}
                  </Type>
                )}
              </div>
            ))}
            <div className="break-inside-avoid pb-2 md:pb-4">
              <Type variant={"h5"} as="span">
                …and many more organizations
              </Type>
            </div>
          </div>
          <div id="add-your-institution">
            <CallToAction
              title="Don't see your organization listed?"
              description="We partner with schools, residencies, foundations, and other cohort-based programs that share our vision for a stronger and more interconnected cultural ecosystem. If the organization you're affiliated with isn't in our network yet, reach out—we'd love to hear from you."
              primaryButtonText="Add Your Organization"
              onPrimaryClick={onAddYourInstitutions}
            />
          </div>
        </MarketingSection>

        {/* ADVISORS */}
        <MarketingSection title="Advisory Council" id="advisors">
          <div className="flex flex-wrap gap-4 md:gap-8">
            {sectionsContent.advisors.map(({ title, description }) => (
              <TextItem
                key={title}
                title={title}
                className="w-full md:w-[calc(50%-1rem)]"
              >
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection>

        {/* PRAISE */}
        <MarketingSection title="Praise" id="praise">
          <div className="flex flex-wrap gap-4 md:gap-x-8 md:gap-y-16">
            {sectionsContent.praise.map(({ title, description }) => (
              <TextItem
                key={title}
                title={title}
                className="w-full md:w-[calc(50%-1rem)] md:pr-24 gap-2"
              >
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection>

        {/* TEAM */}
        <MarketingSection title="Team" id="team">
          <MarketingAbout onHireUsClick={onHireUsClick} />
        </MarketingSection>

        {/* VISION */}
        <MarketingVision onNavigationAnchorClick={onNavigationAnchorClick} />

        {/* NEWSLETTER */}
        <MarketingSection id="newsletter">
          <CallToAction
            title="Get Updates"
            description="Join our newsletter to find out when Pollinator becomes available to you, and to learn about other opportunities."
            theme="light"
            primaryButtonText="Subscribe"
            onPrimaryClick={onNewsletterClick}
          />
        </MarketingSection>

        {/* DONATE */}
        <MarketingSection title="Donate" id="donate">
          <CallToAction
            title="Pollinator is free for alumni of arts and culture organizations in 2026 thanks to generous support from public and private grantmakers."
            primaryButtonText="Donate"
            onPrimaryClick={onDonateClick}
          />
          <div className="flex flex-row mt-4 mb-8 items-center justify-center gap-4 h-fit opacity-50 md:h-12 md:gap-12 md:mt-8 md:justify-start">
            <img
              className="h-8 object-contain md:h-full"
              src={halloranLogo}
              alt="Halloran Philanthropies logo"
            />
            <img
              className="h-8 object-contain md:h-full"
              src={jeromeLogo}
              alt="Jerome Foundation logo"
            />
            <img
              className="h-8 object-contain md:h-full"
              src={swissPhilanthropiesLogo}
              alt="Swiss Philanthropies Foundation logo"
            />
          </div>
        </MarketingSection>
      </div>
    </>
  );
}

export default MarketingContent;
